import CardHeader from "components/CardHeader";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { Asset, AssetServiceInterval } from "models/Asset";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody } from "reactstrap";
import { useDashboardData } from "./DashboardData";

interface DueAssetService {
    asset: Asset;
    interval: AssetServiceInterval;
}

interface Props {
    limit?: number;
}

const AssetServicesCard: React.FC<Props> = ({ limit = 50 }) => {
    const { intlLocale, t } = useOfficeTranslation();
    const { assets, assetsFailed: failed, assetsLoading: loading } = useDashboardData();

    const dueServices = useMemo<DueAssetService[]>(() => assets
        .flatMap(asset => (asset.service_intervals ?? [])
            .filter(interval => interval.due || interval.overdue)
            .map(interval => ({ asset, interval })))
        .sort((a, b) => {
            if (a.interval.overdue !== b.interval.overdue) return a.interval.overdue ? -1 : 1;
            return (a.interval.next_scheduled_at ?? "").localeCompare(b.interval.next_scheduled_at ?? "");
        }), [assets]);

    const formatDate = (value?: string) => value
        ? new Intl.DateTimeFormat(intlLocale, { dateStyle: "medium" }).format(new Date(value))
        : "";

    return <Card className="h-100 mb-0 dashboard-widget-card">
            <CardBody>
                <CardHeader
                    title={t("dashboard.assetServicesCard.dueAssetServices")}
                    icon="mdi-calendar-sync"
                    count={dueServices.length}
                    actions={<Link to="/assets" className="btn btn-sm btn-light">
                        {t("assets.title")} <i className="mdi mdi-arrow-right ms-1" />
                    </Link>}
                />
                {loading && <p className="text-muted mt-3 mb-0">{t("dashboard.assetServicesCard.loading")}</p>}
                {failed && <p className="text-danger mt-3 mb-0">{t("dashboard.assetServicesCard.loadFailed")}</p>}
                {!loading && !failed && dueServices.length === 0 && <p className="text-muted mt-3 mb-0">
                    {t("dashboard.assetServicesCard.noneDue")}
                </p>}
                {!loading && !failed && <div className="dashboard-widget-scroll">{dueServices.slice(0, limit).map(({ asset, interval }) => <div className="border-bottom py-3" key={`${asset.id}-${interval.id}`}>
                    <div className="d-flex justify-content-between align-items-start gap-3">
                        <div className="min-w-0">
                            <Link to={`/assets/${asset.id}`} className="text-body">
                                <strong className="d-block text-truncate">{interval.name}</strong>
                            </Link>
                            <small className="text-muted d-block text-truncate">{asset.name}</small>
                            {interval.next_scheduled_at && <small className="text-muted d-block mt-1">
                                {t("common.nextRun")}: {formatDate(interval.next_scheduled_at)}
                            </small>}
                        </div>
                        <Badge color={interval.overdue ? "danger" : "warning"} className="flex-shrink-0">
                            {t(interval.overdue ? "assets.overdue" : "assets.due")}
                        </Badge>
                    </div>
                </div>)}</div>}
            </CardBody>
        </Card>;
};

export default AssetServicesCard;
